<template>
  <div class="flex flex-col gap-y-10">
    <div
      v-if="isLoading"
      class="font-secondary text-[13px] leading-[20px] text-gray-700"
    >
      Loading document matches...
    </div>

    <div
      v-else-if="errorMessage"
      class="font-secondary text-[13px] leading-[20px] text-red-700"
    >
      {{ errorMessage }}
    </div>

    <template v-else>
      <MatchesDocumentPage
        v-for="page in pages"
        :key="page.page"
        :page="page.page"
        :entries="page.entries"
        :images="page.images"
        @view="emit('view', $event)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import MatchesDocumentPage, {
  type PageMatchEntry,
  type PageMatchImage,
} from './MatchesDocumentPage.vue'
import { ScoreLevel, type Coords } from '@/shared/graphql/router/graphql'
import type { TimelineMatch } from '../timeline-types'
import { computed, ref, watch } from 'vue'

interface DocumentMatchesSummary {
  pages?: DocumentMatchesPage[]
}

interface DocumentMatchesPage {
  page_number?: number
  url?: string
  width?: number
  height?: number
  rendered_width?: number
  rendered_height?: number
  infringing_pairs?: string[]
  matching_pairs?: DocumentMatchingPair[]
}

interface DocumentMatchingPair {
  match_id?: string
  similarity_score?: number
  bboxes?: DocumentMatchBbox[]
}

interface DocumentMatchBbox {
  x1?: number
  y1?: number
  x2?: number
  y2?: number
  page?: number
}

const props = defineProps<{
  summaryUrl?: string | null
  timelineMatchById?: ReadonlyMap<string, TimelineMatch>
}>()

const emit = defineEmits<{
  'update:count': [count: number]
  view: [matchId: string]
}>()

const summary = ref<DocumentMatchesSummary | null>(null)
const isLoading = ref(false)
const errorMessage = ref<string | null>(null)

const fetchSummary = async (url: string, signal: AbortSignal) => {
  const response = await fetch(url, { signal })
  if (!response.ok) {
    throw new Error(`Failed to load document matches (${response.status})`)
  }

  return (await response.json()) as DocumentMatchesSummary
}

watch(
  () => props.summaryUrl,
  (summaryUrl, _previousSummaryUrl, onCleanup) => {
    summary.value = null
    errorMessage.value = null
    isLoading.value = false

    if (!summaryUrl) {
      errorMessage.value = 'Document match summary is unavailable.'
      return
    }

    const controller = new AbortController()
    isLoading.value = true
    onCleanup(() => controller.abort())

    fetchSummary(summaryUrl, controller.signal)
      .then((data) => {
        summary.value = data
      })
      .catch((error) => {
        if (controller.signal.aborted) return

        errorMessage.value =
          error instanceof Error
            ? error.message
            : 'Failed to load document matches.'
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          isLoading.value = false
        }
      })
  },
  { immediate: true }
)

const pages = computed(() => {
  const matchNumbers = new Map<string, number>()
  const lookup = props.timelineMatchById

  return (summary.value?.pages ?? [])
    .map((page, pageIndex) => {
      const pageNumber = page.page_number ?? pageIndex
      const infringingPairIds = new Set(page.infringing_pairs ?? [])
      const matchingPairs = (page.matching_pairs ?? []).filter(
        (pair) =>
          !infringingPairIds.size ||
          (pair.match_id && infringingPairIds.has(pair.match_id))
      )
      const entries = matchingPairs
        .map((pair) => toPageMatchEntry(pair, pageNumber, matchNumbers, lookup))
        .filter((entry): entry is PageMatchEntry => entry !== null)

      const image: PageMatchImage | null = page.url
        ? {
            url: page.url,
            width: page.rendered_width ?? page.width,
            height: page.rendered_height ?? page.height,
            entries,
          }
        : null

      return {
        page: pageNumber,
        entries,
        images: image ? [image] : [],
      }
    })
    .sort((a, b) => a.page - b.page)
})

const matchCount = computed(() => {
  const numbers = new Set<number>()

  for (const page of pages.value) {
    for (const entry of page.entries) {
      numbers.add(entry.number)
    }
  }

  return numbers.size
})

watch(
  matchCount,
  (count) => {
    emit('update:count', count)
  },
  { immediate: true }
)

const toPageMatchEntry = (
  pair: DocumentMatchingPair,
  pageNumber: number,
  matchNumbers: Map<string, number>,
  lookup?: ReadonlyMap<string, TimelineMatch>
): PageMatchEntry | null => {
  const id = pair.match_id
  if (!id) return null

  let number = matchNumbers.get(id)
  if (!number) {
    number = matchNumbers.size + 1
    matchNumbers.set(id, number)
  }

  const coords = normalizeBboxes(pair.bboxes ?? [], pageNumber)
  if (!coords.length) return null

  const level = scoreToLevel(pair.similarity_score)
  const score = isFiniteNumber(pair.similarity_score)
    ? pair.similarity_score * 100
    : 0

  const fromParent = lookup?.get(id)
  const timelineMatch: TimelineMatch = fromParent
    ? { ...fromParent, number }
    : {
        id,
        type: 'Text',
        level: level ?? ScoreLevel.High,
        title: 'Text',
        score,
        number,
        location: '',
        registered: { start: 0, end: 0 },
        infringing: { start: 0, end: 0 },
      }

  return {
    number,
    match: {
      id,
      level,
      coords,
      timelineMatch,
    },
  }
}

const normalizeBboxes = (
  bboxes: DocumentMatchBbox[],
  fallbackPage: number
): Coords[] => {
  const seen = new Set<string>()
  const coords: Coords[] = []

  for (const bbox of bboxes) {
    const { x1, x2, y1, y2 } = bbox
    const page = bbox.page ?? fallbackPage

    if (
      !isFiniteNumber(x1) ||
      !isFiniteNumber(x2) ||
      !isFiniteNumber(y1) ||
      !isFiniteNumber(y2) ||
      !isFiniteNumber(page)
    ) {
      continue
    }

    const coord: Coords = {
      x1,
      x2,
      y1,
      y2,
      page,
    }
    const key = `${coord.page}:${coord.x1}:${coord.y1}:${coord.x2}:${coord.y2}`

    if (seen.has(key)) continue
    seen.add(key)
    coords.push(coord)
  }

  return coords
}

const scoreToLevel = (score?: number): ScoreLevel | null => {
  if (!isFiniteNumber(score)) return null
  if (score >= 0.9) return ScoreLevel.High
  if (score >= 0.75) return ScoreLevel.Medium
  return ScoreLevel.Low
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)
</script>
